import { useMemo, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import type { ContaStoriesMonitorada, Story } from "@base/contracts";
import {
  ArrowLeft,
  Clapperboard,
  Download,
  ExternalLink,
  ListFilter,
  RefreshCw,
  Search,
  Star,
  Trash2,
  Users,
} from "lucide-react";
import {
  Botao,
  CampoDeTexto,
  Esqueleto,
  EstadoDeErro,
  EstadoVazio,
  MolduraDoApp,
} from "@base/ui";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PageLayout } from "@/components/layout/page-header";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { useAuth } from "../../../shared/auth/auth.contexto";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { useDialogos } from "../../../shared/ui/dialogos.contexto";
import {
  useAtualizarFlagsStory,
  useListarContasStoriesMonitoradas,
  useListarStoriesInfinito,
  useSincronizarStories,
  useStatusSessaoInstagram,
  useStatusSincronizacaoStories,
} from "../application/use-referencias";
import { baixarMidiasDaReferencia } from "./baixar-midias-da-referencia";
import { MidiaAutenticada } from "./midia-autenticada";
import { useNavegacaoDoApp } from "./navegacao-do-app";
import { rodapeDaSessaoNaLateral } from "./rodape-da-sessao";
import {
  type OrdenacaoDePerfis,
  ordenarPerfis,
} from "./ordenacao-de-perfis";
import {
  OPCOES_DE_ORDENACAO_PERFIS,
  SeletorDeOrdenacao,
} from "./seletor-de-ordenacao";

/**
 * Feed de stories monitorados: escolhe perfil(is), depois filtra e lista.
 */
export function PaginaDeStories() {
  const [contaIds, definirContaIds] = useState<string[]>([]);
  const [busca, definirBusca] = useState("");
  const [mediaType, definirMediaType] = useState<"image" | "video" | undefined>(
    undefined,
  );
  const [soFavoritos, definirSoFavoritos] = useState(false);
  const [aberta, definirAberta] = useState<Story | null>(null);
  const [ordemDosPerfis, definirOrdemDosPerfis] =
    useState<OrdenacaoDePerfis>("atualizado");

  const contas = useListarContasStoriesMonitoradas();
  const temSelecao = contaIds.length > 0;
  const termo = busca.trim();
  const filtro = {
    ...(temSelecao ? { contaIds } : {}),
    ...(termo.length > 0 ? { q: termo } : {}),
    ...(mediaType ? { mediaType } : {}),
    ...(soFavoritos ? { soFavoritos: true } : {}),
  };
  const perfis = useMemo(
    () => ordenarPerfis(contas.data ?? [], ordemDosPerfis),
    [contas.data, ordemDosPerfis],
  );

  const lista = useListarStoriesInfinito(filtro);
  const sincronizacao = useStatusSincronizacaoStories();
  const sincronizar = useSincronizarStories();
  const sessaoIg = useStatusSessaoInstagram();

  const stories = useMemo(
    () => lista.data?.pages.flatMap((pagina) => pagina.dados) ?? [],
    [lista.data],
  );
  const rodando =
    sincronizacao.data?.running === true || sincronizar.isPending;
  const statusSync = sincronizacao.data?.status ?? "idle";
  const breakerAte = sessaoIg.data?.breakerAte ?? null;
  const emPausaSeguranca = Boolean(breakerAte);
  const podeSincronizar =
    !rodando && !emPausaSeguranca && (contas.data?.length ?? 0) > 0;

  const seletorProjeto = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("stories");
  const totalContas = contas.data?.length ?? 0;
  const filtrosAtivos =
    (mediaType ? 1 : 0) +
    (soFavoritos ? 1 : 0) +
    (totalContas > 0 && contaIds.length < totalContas ? 1 : 0);

  function alternarConta(id: string) {
    definirContaIds((atual) => {
      if (atual.includes(id)) {
        if (atual.length <= 1) return atual;
        return atual.filter((item) => item !== id);
      }
      return [...atual, id];
    });
  }

  function escolherSoEssa(id: string) {
    definirContaIds([id]);
  }

  function voltarParaPerfis() {
    definirContaIds([]);
    definirBusca("");
    definirMediaType(undefined);
  }

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Stories"
      subtitulo="Stories das contas que você monitora"
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletorProjeto}
      acoes={
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant={
              emPausaSeguranca || statusSync === "error"
                ? "destructive"
                : statusSync === "success"
                  ? "secondary"
                  : "outline"
            }
          >
            {emPausaSeguranca
              ? "Pausa por segurança"
              : rodando
                ? "Sincronizando…"
                : statusSync === "success"
                  ? "Atualizado"
                  : statusSync === "error"
                    ? "Falhou"
                    : "Pronto"}
          </Badge>
          {!rodando && sincronizacao.data?.finishedAt ? (
            <span className="text-xs text-muted-foreground">
              Última atualização:{" "}
              {new Date(sincronizacao.data.finishedAt).toLocaleString(
                "pt-BR",
              )}
            </span>
          ) : null}
          <Button
            onClick={() => sincronizar.mutate()}
            disabled={!podeSincronizar}
            loading={rodando}
            title={
              emPausaSeguranca && breakerAte
                ? `Instagram pediu pausa; tente depois de ${new Date(breakerAte).toLocaleString("pt-BR")}`
                : "Sync automático fica espaçado de propósito para proteger a conta"
            }
          >
            <RefreshCw className="size-4" aria-hidden="true" />
            {rodando ? "Sincronizando…" : "Atualizar agora"}
          </Button>
        </div>
      }
    >
      <PageLayout className="animate-aparecer">
        {emPausaSeguranca && breakerAte ? (
          <p className="mb-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            Instagram pediu pausa. Sync desligado até{" "}
            {new Date(breakerAte).toLocaleString("pt-BR")}.
          </p>
        ) : null}
        <div className="space-y-3">
          {temSelecao ? (
            <Botao
              variante="contorno"
              tamanho="pequeno"
              onClick={voltarParaPerfis}
            >
              <ArrowLeft aria-hidden="true" />
              Voltar
            </Botao>
          ) : null}
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-3xl">
              {temSelecao
                ? rotuloSelecao(contaIds, contas.data ?? [])
                : "Escolha um perfil"}
            </h2>
            <p className="text-sm text-muted-foreground sm:text-base">
              {temSelecao
                ? `${stories.length} stor${stories.length === 1 ? "y" : "ies"}`
                : "Selecione um perfil monitorado para liberar o feed e os filtros."}
            </p>
          </div>
        </div>

        {contas.isPending ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Esqueleto key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        ) : null}

        {contas.isError ? (
          <EstadoDeErro
            mensagem={mensagemDoErro(contas.error)}
            aoTentarNovamente={() => void contas.refetch()}
          />
        ) : null}

        {contas.isSuccess && (contas.data?.length ?? 0) === 0 ? (
          <EstadoVazio
            icone={<Users />}
            titulo="Nenhum perfil monitorado"
            descricao="Em Configurações da conta → Instagram (estúdio), marque as contas que você quer acompanhar em Stories."
          />
        ) : null}

        {contas.isSuccess && (contas.data?.length ?? 0) > 0 && !temSelecao ? (
          <div className="space-y-4">
            <div className="flex justify-end">
              <SeletorDeOrdenacao
                valor={ordemDosPerfis}
                opcoes={OPCOES_DE_ORDENACAO_PERFIS}
                onChange={definirOrdemDosPerfis}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {perfis.map((conta) => (
              <button
                key={conta.id}
                type="button"
                onClick={() => escolherSoEssa(conta.id)}
                className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <AvatarContaStories
                  profilePicUrl={conta.profilePicUrl}
                  username={conta.username}
                />
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">
                    @{conta.username}
                  </p>
                  {conta.fullName ? (
                    <p className="truncate text-sm text-muted-foreground">
                      {conta.fullName}
                    </p>
                  ) : null}
                  <p className="truncate text-xs text-muted-foreground">
                    {conta.ultimaAtualizacaoEm
                      ? `Atualizado em ${new Date(conta.ultimaAtualizacaoEm).toLocaleString("pt-BR")}`
                      : "Ainda sem sync"}
                  </p>
                </div>
              </button>
            ))}
            </div>
          </div>
        ) : null}

        {temSelecao ? (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <CampoDeTexto
                  rotulo="Buscar"
                  placeholder="Usuário ou legenda"
                  value={busca}
                  onChange={(evento) => definirBusca(evento.target.value)}
                  icone={<Search aria-hidden="true" />}
                />
              </div>
              <Button
                type="button"
                variant={soFavoritos ? "ink" : "outline"}
                className="h-11 shrink-0 gap-2"
                aria-pressed={soFavoritos}
                onClick={() => definirSoFavoritos((atual) => !atual)}
              >
                <Star
                  className="size-4"
                  aria-hidden="true"
                  fill={soFavoritos ? "currentColor" : "none"}
                />
                Favoritos
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="shrink-0 gap-2"
                    aria-label="Abrir filtros"
                  >
                    <ListFilter className="size-4" aria-hidden="true" />
                    Filtros
                    {filtrosAtivos > 0 ? (
                      <Badge variant="secondary" className="px-1.5 py-0">
                        {filtrosAtivos}
                      </Badge>
                    ) : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-[min(100vw-2rem,22rem)] p-0"
                >
                  <FiltrosStoriesPainel
                    contas={perfis}
                    contaIds={contaIds}
                    mediaType={mediaType}
                    aoAlternarConta={alternarConta}
                    aoDefinirMediaType={definirMediaType}
                    aoLimpar={() => {
                      definirMediaType(undefined);
                      if (contas.data?.length) {
                        definirContaIds(contas.data.map((conta) => conta.id));
                      }
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {filtrosAtivos > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                {mediaType ? (
                  <Badge variant="outline">
                    {mediaType === "video" ? "Vídeo" : "Foto"}
                  </Badge>
                ) : null}
                {contaIds.length < totalContas ? (
                  <Badge variant="outline">
                    {contaIds.length === 1
                      ? `@${contas.data?.find((c) => c.id === contaIds[0])?.username ?? "perfil"}`
                      : `${contaIds.length} perfis`}
                  </Badge>
                ) : null}
              </div>
            ) : null}

            {lista.isPending && stories.length === 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Esqueleto
                    key={i}
                    className="aspect-[9/16] w-full rounded-xl"
                  />
                ))}
              </div>
            ) : null}

            {lista.isError && stories.length === 0 ? (
              <EstadoDeErro
                mensagem={mensagemDoErro(lista.error)}
                aoTentarNovamente={() => void lista.refetch()}
              />
            ) : null}

            {lista.isSuccess && stories.length === 0 ? (
              <EstadoVazio
                icone={<Clapperboard />}
                titulo="Nenhum story ainda"
                descricao="Toque em Atualizar agora para baixar os stories das contas selecionadas."
              />
            ) : null}

            {stories.length > 0 ? (
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {stories.map((story) => (
                  <li key={story.id}>
                    <TileDeStory
                      story={story}
                      aoAbrir={() => definirAberta(story)}
                      aoFecharSeAberta={() => {
                        if (aberta?.id === story.id) definirAberta(null);
                      }}
                    />
                  </li>
                ))}
              </ul>
            ) : null}

            {lista.hasNextPage ? (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => void lista.fetchNextPage()}
                  loading={lista.isFetchingNextPage}
                >
                  Carregar mais
                </Button>
              </div>
            ) : null}
          </>
        ) : null}

        {aberta
          ? createPortal(
              <LightboxStory
                story={aberta}
                aoFechar={() => definirAberta(null)}
                aoAtualizar={(story) => definirAberta(story)}
              />,
              document.body,
            )
          : null}
      </PageLayout>
    </MolduraDoApp>
  );
}

function FiltrosStoriesPainel({
  contas,
  contaIds,
  mediaType,
  aoAlternarConta,
  aoDefinirMediaType,
  aoLimpar,
}: {
  readonly contas: readonly ContaStoriesMonitorada[];
  readonly contaIds: readonly string[];
  readonly mediaType: "image" | "video" | undefined;
  readonly aoAlternarConta: (id: string) => void;
  readonly aoDefinirMediaType: (tipo: "image" | "video" | undefined) => void;
  readonly aoLimpar: () => void;
}) {
  const [buscaPerfil, definirBuscaPerfil] = useState("");
  const termoPerfil = buscaPerfil.trim().toLowerCase();
  const contasFiltradas = useMemo(() => {
    if (!termoPerfil) return contas;
    return contas.filter(
      (conta) =>
        conta.username.toLowerCase().includes(termoPerfil) ||
        (conta.fullName?.toLowerCase().includes(termoPerfil) ?? false),
    );
  }, [contas, termoPerfil]);

  const temFiltro =
    mediaType !== undefined ||
    (contas.length > 0 && contaIds.length < contas.length);

  return (
    <div className="flex h-[min(72vh,30rem)] w-full flex-col overflow-hidden">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <p className="text-sm font-medium text-foreground">Filtros</p>
        <p className="text-xs text-muted-foreground">
          Refine o feed sem poluir a tela.
        </p>
      </div>

      <fieldset className="shrink-0 space-y-2 border-b border-border px-4 py-3">
        <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Tipo de mídia
        </legend>
        <div className="flex flex-wrap gap-2" role="group">
          <Botao
            tamanho="pequeno"
            variante={mediaType === undefined ? "tinta" : "contorno"}
            aria-pressed={mediaType === undefined}
            onClick={() => aoDefinirMediaType(undefined)}
          >
            Todos
          </Botao>
          <Botao
            tamanho="pequeno"
            variante={mediaType === "image" ? "tinta" : "contorno"}
            aria-pressed={mediaType === "image"}
            onClick={() => aoDefinirMediaType("image")}
          >
            Foto
          </Botao>
          <Botao
            tamanho="pequeno"
            variante={mediaType === "video" ? "tinta" : "contorno"}
            aria-pressed={mediaType === "video"}
            onClick={() => aoDefinirMediaType("video")}
          >
            Vídeo
          </Botao>
        </div>
      </fieldset>

      <section
        className="flex min-h-0 flex-1 flex-col"
        aria-labelledby="filtros-stories-perfil"
      >
        <div className="shrink-0 space-y-2 px-4 pt-3 pb-2">
          <div className="flex items-center justify-between gap-2">
            <h3
              id="filtros-stories-perfil"
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Perfil
            </h3>
            <span className="text-xs tabular-nums text-muted-foreground">
              {contaIds.length}/{contas.length}
            </span>
          </div>
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={buscaPerfil}
              onChange={(evento) => definirBuscaPerfil(evento.target.value)}
              placeholder="Buscar perfil"
              aria-label="Buscar perfil"
              className="h-9 pl-8"
            />
          </div>
        </div>

        <ul
          className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-4 pb-2"
          aria-label="Perfis monitorados"
        >
          {contasFiltradas.length === 0 ? (
            <li className="px-1 py-6 text-center text-sm text-muted-foreground">
              Nenhum perfil com esse termo.
            </li>
          ) : (
            contasFiltradas.map((conta) => {
              const selecionada = contaIds.includes(conta.id);
              const id = `filtro-conta-${conta.id}`;
              return (
                <li key={conta.id}>
                  <Label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-3 rounded-lg border border-transparent px-1 py-1.5 hover:bg-muted/50"
                  >
                    <Checkbox
                      id={id}
                      checked={selecionada}
                      onCheckedChange={() => aoAlternarConta(conta.id)}
                    />
                    <AvatarContaStories
                      profilePicUrl={conta.profilePicUrl}
                      username={conta.username}
                      tamanho="size-8"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">
                        @{conta.username}
                      </span>
                      {conta.fullName ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {conta.fullName}
                        </span>
                      ) : null}
                    </span>
                  </Label>
                </li>
              );
            })
          )}
        </ul>
      </section>

      {temFiltro ? (
        <div className="shrink-0 border-t border-border px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={aoLimpar}
          >
            Limpar filtros
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function AvatarContaStories({
  profilePicUrl,
  username,
  tamanho = "size-11",
}: {
  readonly profilePicUrl: string | null;
  readonly username: string;
  readonly tamanho?: string;
}) {
  const inicial = username.slice(0, 2).toUpperCase();
  const fallback = (
    <span
      className={`grid ${tamanho} shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-muted-foreground`}
      aria-hidden="true"
    >
      {inicial}
    </span>
  );

  if (!profilePicUrl) return fallback;

  return (
    <MidiaAutenticada
      src={profilePicUrl}
      className={`${tamanho} shrink-0 rounded-full object-cover`}
      fallback={fallback}
    />
  );
}

function TileDeStory({
  story,
  aoAbrir,
  aoFecharSeAberta,
}: {
  readonly story: Story;
  readonly aoAbrir: () => void;
  readonly aoFecharSeAberta: () => void;
}) {
  const { tokenAtual } = useAuth();
  const { avisar, confirmar } = useDialogos();
  const flags = useAtualizarFlagsStory();
  const [baixando, definirBaixando] = useState(false);
  const temMidia = Boolean(
    story.imageUrl || story.videoUrl || story.images.length > 0,
  );

  const aoBaixar = async (evento: MouseEvent<HTMLButtonElement>) => {
    evento.preventDefault();
    evento.stopPropagation();
    if (!temMidia || baixando) return;
    definirBaixando(true);
    try {
      const qtd = await baixarMidiasDaReferencia(story, tokenAtual());
      if (qtd === 0) {
        await avisar({
          titulo: "Nada para baixar",
          descricao: "Este story ainda não tem mídia salva.",
        });
      }
    } catch {
      await avisar({
        titulo: "Não deu para baixar",
        descricao: "Tente de novo em instantes.",
      });
    } finally {
      definirBaixando(false);
    }
  };

  const aoFavoritar = (evento: MouseEvent<HTMLButtonElement>) => {
    evento.preventDefault();
    evento.stopPropagation();
    flags.mutate({ id: story.id, favorito: !story.favorito });
  };

  const aoExcluir = async (evento: MouseEvent<HTMLButtonElement>) => {
    evento.preventDefault();
    evento.stopPropagation();
    const ok = await confirmar({
      titulo: "Excluir deste feed?",
      descricao:
        "Some só daqui. O story continua no Instagram se ainda estiver no ar.",
      rotuloConfirmar: "Excluir",
    });
    if (!ok) return;
    flags.mutate(
      { id: story.id, oculto: true },
      { onSuccess: () => aoFecharSeAberta() },
    );
  };

  return (
    <div className="group relative aspect-[9/16] w-full overflow-hidden rounded-xl border border-border bg-muted">
      <div className="absolute left-2 top-2 z-[10] flex flex-col gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
        {temMidia ? (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="size-8 rounded-md bg-background/95 shadow-xs hover:bg-background"
            aria-label="Baixar mídia"
            title="Baixar mídia"
            disabled={baixando}
            onClick={(evento) => {
              void aoBaixar(evento);
            }}
          >
            <Download className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="size-8 rounded-md bg-background/95 shadow-xs hover:bg-background"
          aria-label={story.favorito ? "Remover dos favoritos" : "Favoritar"}
          aria-pressed={story.favorito}
          disabled={flags.isPending}
          onClick={aoFavoritar}
        >
          <Star
            className="size-4"
            aria-hidden="true"
            fill={story.favorito ? "currentColor" : "none"}
          />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="size-8 rounded-md bg-background/95 shadow-xs hover:bg-background"
          aria-label="Excluir do feed"
          disabled={flags.isPending}
          onClick={(evento) => {
            void aoExcluir(evento);
          }}
        >
          <Trash2 className="size-4" aria-hidden="true" />
        </Button>
      </div>
      <button
        type="button"
        onClick={aoAbrir}
        className="size-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <MidiaAutenticada
          src={story.imageUrl ?? story.images[0] ?? null}
          className="size-full object-cover transition-transform group-hover:scale-[1.02]"
          fallback={
            <span className="flex size-full items-center justify-center text-muted-foreground">
              <Clapperboard className="size-8" />
            </span>
          }
        />
        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2.5 py-2 text-xs text-white">
          @{story.username ?? story.contaId}
        </span>
      </button>
    </div>
  );
}

function LightboxStory({
  story,
  aoFechar,
  aoAtualizar,
}: {
  readonly story: Story;
  readonly aoFechar: () => void;
  readonly aoAtualizar: (story: Story) => void;
}) {
  const { confirmar } = useDialogos();
  const flags = useAtualizarFlagsStory();

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/80 p-4"
      style={{ zIndex: "var(--rk-z-modal)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Story"
      onClick={aoFechar}
      onKeyDown={(evento) => {
        if (evento.key === "Escape") aoFechar();
      }}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-md overflow-hidden rounded-2xl bg-card"
        onClick={(evento) => evento.stopPropagation()}
      >
        {story.videoUrl ? (
          <MidiaAutenticada
            src={story.videoUrl}
            tipo="video"
            controls
            className="max-h-[80vh] w-full object-contain"
          />
        ) : (
          <MidiaAutenticada
            src={story.imageUrl ?? story.images[0] ?? null}
            className="max-h-[80vh] w-full object-contain"
          />
        )}
        <div className="flex items-center gap-3 border-t border-border px-4 py-3">
          <AvatarContaStories
            profilePicUrl={story.profilePicUrl}
            username={story.username ?? story.contaId}
            tamanho="size-9"
          />
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="truncate font-medium text-foreground">
              @{story.username ?? story.contaId}
            </p>
            {story.takenAt ? (
              <p className="text-xs text-muted-foreground">
                Postado em {new Date(story.takenAt).toLocaleString("pt-BR")}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              Adicionado em {new Date(story.syncedAt).toLocaleString("pt-BR")}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8"
              aria-label={
                story.favorito ? "Remover dos favoritos" : "Favoritar"
              }
              aria-pressed={story.favorito}
              disabled={flags.isPending}
              onClick={() => {
                flags.mutate(
                  { id: story.id, favorito: !story.favorito },
                  {
                    onSuccess: (atualizado) => aoAtualizar(atualizado),
                  },
                );
              }}
            >
              <Star
                className="size-4"
                aria-hidden="true"
                fill={story.favorito ? "currentColor" : "none"}
              />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8"
              aria-label="Excluir do feed"
              disabled={flags.isPending}
              onClick={() => {
                void (async () => {
                  const ok = await confirmar({
                    titulo: "Excluir deste feed?",
                    descricao:
                      "Some só daqui. O story continua no Instagram se ainda estiver no ar.",
                    rotuloConfirmar: "Excluir",
                  });
                  if (!ok) return;
                  flags.mutate(
                    { id: story.id, oculto: true },
                    { onSuccess: () => aoFechar() },
                  );
                })();
              }}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </Button>
            {story.conteudoCompartilhadoUrl ? (
              <Button variant="default" size="sm" asChild>
                <a
                  href={story.conteudoCompartilhadoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(evento) => evento.stopPropagation()}
                >
                  <ExternalLink className="size-3.5" aria-hidden="true" />
                  {rotuloConteudoCompartilhado(story.conteudoCompartilhadoTipo)}
                </a>
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={aoFechar}>
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function rotuloConteudoCompartilhado(
  tipo: Story["conteudoCompartilhadoTipo"],
): string {
  if (tipo === "reel") return "Ver Reel original";
  if (tipo === "post") return "Ver post original";
  return "Acessar conteúdo compartilhado";
}

function rotuloSelecao(
  ids: readonly string[],
  contas: readonly ContaStoriesMonitorada[],
): string {
  if (ids.length === 1) {
    const conta = contas.find((c) => c.id === ids[0]);
    return conta ? `@${conta.username}` : "1 perfil";
  }
  return `${ids.length} perfis`;
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroDaApi) return erro.message;
  if (erro instanceof Error) return erro.message;
  return "Não foi possível carregar os stories.";
}
